import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileEdit, X } from "lucide-react";
import { toast } from "react-toastify";
import { empreendimentoService } from "../../services/empreendimentoService";
import { incorporadoraService } from "../../services/incorporadoraService";
import { municipioService } from "../../services/enderecoService";
import { rascunhoEmpreendimentoService } from "../../services/rascunhoEmpreendimentoService";
import { Empreendimento } from "../../types/empreendimento";
import { Incorporadora } from "../../types/incorporadora";
import { Municipio } from "../../types/localidade"; // Importar Municipio
import { RascunhoResumo } from "../../types/rascunhoEmpreendimento";
import { PaginatedTable } from "../../components/Table/PaginatedTable";
import MovimentacaoModal from "./componentes/MovimentacaoModal";

// Formatação relativa simples ("há 5 min", "há 2h", "há 3 dias") — não
// precisa de nada além disso pra um rascunho que expira em 7 dias.
const formatarTempoRelativo = (isoDate: string): string => {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "agora mesmo";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHoras = Math.floor(diffMin / 60);
  if (diffHoras < 24) return `há ${diffHoras}h`;
  const diffDias = Math.floor(diffHoras / 24);
  return `há ${diffDias} dia${diffDias > 1 ? "s" : ""}`;
};

/**
 * Listagem de Empreendimentos com Filtros Backend
 *
 * Agora utiliza filtros implementados no backend para melhor performance
 * e escalabilidade.
 */
const ListaEmpreendimentos: React.FC = () => {
  const [empreendimentos, setEmpreendimentos] = useState<Empreendimento[]>([]);
  const [incorporadoras, setIncorporadoras] = useState<Incorporadora[]>([]);
  const [municipiosFilter, setMunicipiosFilter] = useState<Municipio[]>([]); // Renomeado para evitar conflito
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    incorporadora: "", // ID da incorporadora
    cidade: "", // ID do município
  });

  // estado para movimentação
  const [isMovModalOpen, setIsMovModalOpen] = useState(false);
  const [selectedEmpreendimento, setSelectedEmpreendimento] =
    useState<Empreendimento | null>(null);

  // id do empreendimento cujo CSV está sendo gerado (para desabilitar o botão)
  const [exportandoId, setExportandoId] = useState<number | null>(null);
  const [exportandoGeral, setExportandoGeral] = useState(false);

  // Rascunhos de "Novo Empreendimento" salvos localmente (localStorage) —
  // ver rascunhoEmpreendimentoService. Não vêm do backend, então não entram
  // na tabela paginada: aparecem numa seção própria, visualmente distinta.
  const [rascunhos, setRascunhos] = useState<RascunhoResumo[]>([]);

  useEffect(() => {
    setRascunhos(rascunhoEmpreendimentoService.listar());
  }, []);

  const handleDescartarRascunho = (id: string) => {
    if (!window.confirm("Descartar este rascunho? Os dados preenchidos serão perdidos.")) return;
    rascunhoEmpreendimentoService.remover(id);
    setRascunhos((prev) => prev.filter((r) => r.id !== id));
  };

  // Buscar dados de referência (incorporadoras e cidades)
  const fetchReferenceData = async () => {
    try {
      const [incorporadorasData, municipiosPaginatedData] = await Promise.all([
        incorporadoraService.listarParaSelecao(),
        municipioService.listar({ limit: 1000 }), // Buscar todos os municípios para o filtro
      ]);

      setIncorporadoras(incorporadorasData);
      setMunicipiosFilter(municipiosPaginatedData || []); // Armazena os municípios para o filtro
    } catch (error) {
      console.error("Erro ao buscar dados de referência:", error);
      toast.error("Erro ao carregar incorporadoras e cidades para os filtros.");
    }
  };

  // Função para obter nome da incorporadora
  const getIncorporadoraNome = (incorporadoraId: number) => {
    const incorporadora = incorporadoras.find(
      (inc) => inc.id === incorporadoraId
    );
    const nome =
      incorporadora?.razao_social || `Incorporadora ${incorporadoraId}`;

    return nome;
  };

  // Função para obter nome da cidade a partir do objeto EmpreendimentoEndereco
  const getCidadeNome = (endereco: Empreendimento["endereco"] | undefined) => {
    if (!endereco || !endereco.logradouro || !endereco.logradouro.cidade) {
      return "Não informado";
    }

    // A propriedade `cidade` em `EmpreendimentoEndereco` já é um objeto `CidadeDetail`
    // que contém `nome` e `uf.sigla`.
    const cidadeObj = endereco.logradouro.cidade;
    if (cidadeObj.nome && cidadeObj.uf) {
      return `${cidadeObj.nome}/${cidadeObj.uf.sigla}`;
    }

    // Fallback caso a estrutura não seja a esperada
    return "Endereço não disponível";
  };

  // Buscar empreendimentos com filtros do backend
  const fetchEmpreendimentos = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...(filters.search && { search: filters.search }), // Filtro de busca por nome
        ...(filters.incorporadora && {
          incorporadora: parseInt(filters.incorporadora),
        }),
        ...(filters.cidade && { cidade: parseInt(filters.cidade) }),
      };

      const response = await empreendimentoService.list(params);

      setEmpreendimentos(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Erro ao buscar empreendimentos:", error);
      toast.error("Erro ao carregar a lista de empreendimentos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchEmpreendimentos();
  }, [currentPage, pageSize, filters]); // Agora refaz a busca quando filtros mudam

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset para primeira página ao filtrar
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir este empreendimento?")) {
      try {
        await empreendimentoService.delete(id);
        // Recarregar dados após exclusão
        fetchEmpreendimentos();
      } catch (error) {
        console.error("Erro ao excluir empreendimento:", error);
        toast.error("Erro ao excluir empreendimento.");
      }
    }
  };

  const openMovimentacao = (emp: Empreendimento) => {
    setSelectedEmpreendimento(emp);
    setIsMovModalOpen(true);
  };

  const handleExportarCsv = async (emp: Empreendimento) => {
    setExportandoId(emp.id);
    try {
      await empreendimentoService.exportarCsv(emp.id);
    } catch (error) {
      console.error("Erro ao exportar CSV do empreendimento:", error);
      toast.error("Erro ao exportar o CSV do empreendimento.");
    } finally {
      setExportandoId(null);
    }
  };

  // Exporta todo o sistema, aplicando os mesmos filtros que estão na tela.
  const handleExportarGeral = async () => {
    const filtrosAtivos = [
      filters.search && "nome",
      filters.incorporadora && "incorporadora",
      filters.cidade && "cidade",
    ].filter(Boolean);

    const aviso = filtrosAtivos.length
      ? `O export vai respeitar os filtros ativos (${filtrosAtivos.join(
          ", "
        )}). Continuar?`
      : "Isso vai exportar todos os empreendimentos aos quais você tem acesso, com unidades, movimentações e consolidações. Pode demorar. Continuar?";

    if (!window.confirm(aviso)) return;

    setExportandoGeral(true);
    try {
      await empreendimentoService.exportarCsvGeral({
        ...(filters.search && { search: filters.search }),
        ...(filters.incorporadora && {
          incorporadora: parseInt(filters.incorporadora),
        }),
        ...(filters.cidade && { cidade: parseInt(filters.cidade) }),
      });
    } catch (error) {
      console.error("Erro ao exportar CSV geral:", error);
      toast.error("Erro ao gerar o export geral.");
    } finally {
      setExportandoGeral(false);
    }
  };

  const columns = [
    {
      key: "incorporadora" as keyof Empreendimento,
      title: "Incorporadora",
      render: (value: any, empreendimento: Empreendimento) => (
        <span className="font-medium text-gray-900">
          {getIncorporadoraNome(empreendimento.incorporadora)}
        </span>
      ),
    },
    {
      key: "nome" as keyof Empreendimento,
      title: "Nome do Empreendimento",
      render: (value: any, empreendimento: Empreendimento) => (
        <Link
          to={`/empreendimentos/${empreendimento.id}/visualizar`}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          {empreendimento.nome}
        </Link>
      ),
    },
    {
      key: "endereco" as keyof Empreendimento,
      title: "Cidade",
      render: (value: any, empreendimento: Empreendimento) => (
        <span className="text-gray-700">
          {getCidadeNome(empreendimento.endereco)}
        </span>
      ),
    },
    {
      key: "data_entrega" as keyof Empreendimento,
      title: "Data de Entrega",
      render: (value: any, empreendimento: Empreendimento) => (
        <span>
          {empreendimento.data_entrega
            ? new Date(empreendimento.data_entrega).toLocaleDateString("pt-BR")
            : "Não informado"}
        </span>
      ),
    },
    {
      key: "id" as keyof Empreendimento,
      title: "Ações",
      render: (value: any, empreendimento: Empreendimento) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleDelete(empreendimento.id)}
            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
          >
            Excluir
          </button>
          <button
            onClick={() => openMovimentacao(empreendimento)}
            className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
          >
            Movimentar
          </button>
          <button
            onClick={() => handleExportarCsv(empreendimento)}
            disabled={exportandoId === empreendimento.id}
            title="Baixa um CSV com dados, unidades, movimentações e consolidações"
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportandoId === empreendimento.id ? "Gerando..." : "Exportar CSV"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Empreendimentos</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleExportarGeral}
            disabled={exportandoGeral}
            title="Exporta todos os empreendimentos, unidades, movimentações e consolidações em um único CSV"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportandoGeral ? "Gerando export..." : "Exportar tudo (CSV)"}
          </button>
          <Link
            to="/empreendimentos/novo"
            title="Inclui a opção de importar com IA (book/tabela de preço) na primeira etapa"
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Sparkles size={16} />
            Novo Empreendimento
          </Link>
        </div>
      </div>

      {/* Rascunhos locais (não salvos no backend ainda) */}
      {rascunhos.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Rascunhos em andamento
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rascunhos.map((rascunho) => (
              <div
                key={rascunho.id}
                className="flex items-center justify-between gap-3 bg-amber-50/60 border border-dashed border-amber-300 rounded-lg px-4 py-3 opacity-90"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileEdit size={14} className="text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Rascunho
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 truncate mt-1">
                    {rascunho.nome?.trim() || "Sem nome ainda"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Editado {formatarTempoRelativo(rascunho.atualizadoEm)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Link
                    to={`/empreendimentos/novo?rascunho=${rascunho.id}`}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                  >
                    Continuar
                  </Link>
                  <button
                    onClick={() => handleDescartarRascunho(rascunho.id)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600"
                  >
                    <X size={12} /> Descartar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Empreendimento
            </label>
            <input
              type="text"
              placeholder="Digite o nome do empreendimento..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Incorporadora
            </label>
            <select
              value={filters.incorporadora}
              onChange={(e) =>
                handleFilterChange("incorporadora", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas as incorporadoras</option>
              {incorporadoras.map((incorporadora) => (
                <option key={incorporadora.id} value={incorporadora.id}>
                  {incorporadora.razao_social ||
                    `Incorporadora ${incorporadora.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cidade
            </label>
            <select
              value={filters.cidade} // ID do município
              onChange={(e) => handleFilterChange("cidade", e.target.value)} // value é ID
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas as cidades</option>
              {municipiosFilter.map((cidade) => (
                <option key={cidade.id} value={cidade.id}>
                  {cidade.nome_original}/{cidade.uf.sigla} 
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botão para limpar filtros */}
        <div className="mt-4">
          <button
            onClick={() =>
              setFilters({ search: "", incorporadora: "", cidade: "" })
            }
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <PaginatedTable
          data={empreendimentos}
          columns={columns as any}
          loading={loading}
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / pageSize)}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Modal de movimentação */}
      <MovimentacaoModal
        isOpen={isMovModalOpen}
        onClose={() => setIsMovModalOpen(false)}
        empreendimento={selectedEmpreendimento}
        onSuccess={fetchEmpreendimentos}
      />
    </div>
  );
};
export default ListaEmpreendimentos;
